import React, { useState, useEffect, useRef } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import api from '../api';
import { X, Play, Square, FileText, Bot, Loader2 } from 'lucide-react';

const TestAgentInterface = ({ agent, onClose }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [captions, setCaptions] = useState([]);
    const [room, setRoom] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [error, setError] = useState(null);
    const [webhookEvents, setWebhookEvents] = useState([]);
    
    const audioRef = useRef(null);
    const localAudioTrackRef = useRef(null);
    const transcriptRef = useRef(null);
    const sessionIdRef = useRef(null);

    // Start test session
    const startTestSession = async () => {
        try {
            setIsConnecting(true);
            setError(null);
            
            // Generate unique room name for test with environment prefix
            // Use test-agent-local-* for localhost, test-agent-* for production
            const timestamp = Date.now();
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const roomPrefix = isLocalhost ? 'test-agent-local-' : 'test-agent-';
            const roomName = `${roomPrefix}${agent.id}-${timestamp}`;
            
            
            // Create test session in backend
            const sessionResponse = await api.post('/sessions/test', {
                room_name: roomName,
                agent_id: agent.id,
                channel: 'test'
            });
            
            const testSessionId = sessionResponse.data.session_id;
            setSessionId(testSessionId);
            sessionIdRef.current = testSessionId;

            // Get LiveKit token
            const tokenResponse = await api.post('/token', {
                room: roomName,
                identity: 'test-user'
            });
            
            const { url, token } = tokenResponse.data;
            
            // Create and connect to LiveKit room
            const newRoom = new Room({
                adaptiveStream: true,
                dynacast: true,
                disconnectOnPageLeave: false, // Keep call active when user switches browser tabs
                videoCaptureDefaults: {
                    resolution: { width: 1280, height: 720 }
                }
            });
            
            // Set up event listeners
            newRoom.on(RoomEvent.Connected, () => {
                setIsConnected(true);
                setIsConnecting(false);
                addCaption('system', '✅ Connected to test agent');
            });
            
            newRoom.on(RoomEvent.Disconnected, () => {
                setIsConnected(false);
                addCaption('system', '❌ Disconnected from test agent');
            });

            // When agent disconnects (e.g. end node), update UI in case end_call message was missed
            newRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
                const remoteCount = newRoom.remoteParticipants.size;
                if (remoteCount === 0) {
                    setIsConnected(false);
                    addCaption('system', '📞 Agent left the call');
                }
            });
            
            newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
                if (track.kind === 'audio') {
                    // Play remote audio
                    if (audioRef.current) {
                        track.attach(audioRef.current);
                    }
                }
            });
            
            newRoom.on(RoomEvent.DataReceived, async (payload, participant) => {
                try {
                    const decoder = new TextDecoder();
                    const data = JSON.parse(decoder.decode(payload));
                    
                    // Handle agent-initiated end call (e.g. end node)
                    if (data.type === 'end_call') {
                        addCaption('system', '📞 Call ended by agent');
                        setIsConnected(false);
                        setRoom(null);
                        try {
                            await newRoom.disconnect();
                        } catch (_) {}
                        if (sessionIdRef.current) {
                            try {
                                await api.post(`/sessions/${sessionIdRef.current}/end`);
                            } catch (_) {}
                            sessionIdRef.current = null;
                        }
                        setSessionId(null);
                        return;
                    }
                    
                    // Handle transcription data
                    if (data.type === 'transcription') {
                        addCaption(data.speaker || 'agent', data.text);
                    }
                    
                    // Handle webhook events
                    if (data.type === 'webhook_event') {
                        addWebhookEvent(data.event_type, data.function_name, data.details);
                    }
                    
                    // Handle conversation items
                    if (data.type === 'conversation_item') {
                        addCaption(data.speaker || 'agent', data.text);
                    }
                } catch (e) {
                    console.error('Error parsing data packet:', e);
                }
            });
            
            // Connect to room
            await newRoom.connect(url, token);
            setRoom(newRoom);
            
            // Enable microphone
            await newRoom.localParticipant.setMicrophoneEnabled(true);
            
        } catch (err) {
            console.error('❌ Error starting test session:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to start test session');
            setIsConnecting(false);
        }
    };

    // Stop test session
    const stopTestSession = async () => {
        try {
            if (room) {
                await room.disconnect();
                setRoom(null);
            }
            
            if (sessionId) {
                // End session in backend
                await api.post(`/sessions/${sessionId}/end`);
            }
            
            setIsConnected(false);
            setSessionId(null);
            sessionIdRef.current = null;

        } catch (err) {
            console.error('❌ Error stopping test session:', err);
        }
    };

    // Add caption to transcript
    const addCaption = (speaker, text) => {
        if (!text || !text.trim()) return;
        
        setCaptions(prev => {
            // Deduplicate
            const lastCaption = prev[prev.length - 1];
            if (lastCaption && lastCaption.speaker === speaker && lastCaption.text === text) {
                return prev;
            }
            
            return [...prev, {
                speaker,
                text,
                timestamp: new Date().toLocaleTimeString()
            }];
        });
    };

    // Add webhook event
    const addWebhookEvent = (eventType, functionName, details) => {
        setWebhookEvents(prev => [...prev, {
            eventType,
            functionName,
            details,
            timestamp: new Date().toLocaleTimeString()
        }]);
    };

    // Auto-scroll transcript to bottom when new captions are added
    useEffect(() => {
        if (transcriptRef.current && captions.length > 0) {
            // Use setTimeout to ensure DOM has updated
            setTimeout(() => {
                if (transcriptRef.current) {
                    transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
                }
            }, 0);
        }
    }, [captions]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (room) {
                room.disconnect();
            }
        };
    }, [room]);

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
                {/* Header */}
                <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                            <FileText className="w-4 h-4" />
                        </div>
                    <div>
                            <h2 className="text-lg font-semibold tracking-tight">Test Agent: {agent.name}</h2>
                            <p className="text-white/70 text-xs mt-0.5 font-normal">Test your agent's capabilities in real-time</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex flex-1 overflow-hidden min-h-0">
                    {/* Main content area */}
                    <div className="flex-1 flex flex-col bg-background min-h-0">
                        {/* Connection controls */}
                        <div className="p-5 border-b border-border">
                            <div className="flex items-center gap-3">
                            {!isConnected ? (
                                <button
                                    onClick={startTestSession}
                                    disabled={isConnecting}
                                        className={`px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                                        isConnecting
                                                ? 'bg-muted cursor-not-allowed text-muted-foreground'
                                                : 'bg-success hover:bg-success/90 text-success-foreground shadow-elegant'
                                    }`}
                                >
                                        {isConnecting ? (
                                            <>
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                Connecting...
                                            </>
                                        ) : (
                                            <>
                                                <Play className="w-3.5 h-3.5" />
                                                Start Test Session
                                            </>
                                        )}
                                </button>
                            ) : (
                                <button
                                    onClick={stopTestSession}
                                        className="px-5 py-2.5 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg text-sm font-medium flex items-center gap-2 transition-all shadow-elegant"
                                >
                                        <Square className="w-3.5 h-3.5" />
                                        Stop Test Session
                                </button>
                            )}
                            
                            {isConnected && (
                                    <span className="text-xs text-success font-medium flex items-center gap-1.5">
                                        <div className="w-1.5 h-1.5 bg-success rounded-full animate-pulse"></div>
                                        Connected
                                </span>
                            )}
                            </div>
                            
                            {error && (
                                <div className="mt-3 p-2.5 bg-destructive/10 border border-destructive/30 text-destructive rounded-lg text-xs">
                                    {error}
                                </div>
                            )}
                        </div>

                        {/* Transcript display */}
                        <div className="flex-1 flex flex-col overflow-hidden p-5 min-h-0">
                            <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                                <FileText className="w-4 h-4 text-primary" />
                                <h3 className="text-sm font-semibold text-foreground">Live Transcript</h3>
                            </div>
                            <div 
                                ref={transcriptRef}
                                className="overflow-y-scroll space-y-2.5 pr-2"
                                style={{ 
                                    scrollBehavior: 'smooth',
                                    maxHeight: 'calc(100vh - 350px)',
                                    height: '100%',
                                    minHeight: '300px'
                                }}
                            >
                                {captions.length === 0 ? (
                                    <div className="text-center py-12 rounded-lg border border-dashed border-border bg-muted/10">
                                        <p className="text-muted-foreground text-sm">
                                        No messages yet. Start the test session to begin.
                                        </p>
                                    </div>
                                ) : (
                                    captions.map((caption, index) => (
                                        <div
                                            key={index}
                                            className={`p-3 rounded-lg border border-primary/50 ${
                                                caption.speaker === 'user'
                                                    ? 'bg-primary/10'
                                                    : caption.speaker === 'ai' || caption.speaker === 'agent'
                                                    ? 'bg-secondary-cyan/10'
                                                    : 'bg-warning/10'
                                            }`}
                                            style={{
                                                boxShadow: '0 0 16px hsl(215 90% 58% / 0.4), 0 0 32px hsl(215 90% 58% / 0.2), inset 0 0 8px hsl(215 90% 58% / 0.1)'
                                            }}
                                        >
                                            <div className="flex justify-between items-start gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 mb-1.5">
                                                        <span className={`text-xs font-medium ${
                                                            caption.speaker === 'user'
                                                                ? 'text-primary'
                                                                : caption.speaker === 'ai' || caption.speaker === 'agent'
                                                                ? 'text-secondary-cyan'
                                                                : 'text-warning'
                                                        }`}>
                                                            {caption.speaker === 'user' ? 'You' : 
                                                             caption.speaker === 'ai' || caption.speaker === 'agent' ? 'Agent' : 
                                                             'System'}
                                                    </span>
                                                    </div>
                                                    <p className="text-sm text-foreground leading-relaxed">{caption.text}</p>
                                                </div>
                                                <span className="text-xs text-muted-foreground flex-shrink-0">
                                                    {caption.timestamp}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Agent Info Sidebar */}
                    <div className="w-72 bg-card border-l border-border p-5 overflow-y-auto">
                        <div className="flex items-center gap-2 mb-4">
                            <Bot className="w-4 h-4 text-primary" />
                            <h3 className="text-sm font-semibold text-foreground">Agent Info</h3>
                        </div>
                        
                        <div className="p-3.5 bg-muted/10 border border-border rounded-lg space-y-3">
                            <div>
                                <span className="text-xs text-muted-foreground font-medium">Model</span>
                                <p className="text-sm text-foreground mt-1 font-mono">{agent.llm_model || 'N/A'}</p>
                            </div>
                            <div>
                                <span className="text-xs text-muted-foreground font-medium">Voice</span>
                                <p className="text-sm text-foreground mt-1 capitalize">{agent.voice_provider || 'cartesia'}</p>
                            </div>
                            <div>
                                <span className="text-xs text-muted-foreground font-medium">Temperature</span>
                                <p className="text-sm text-foreground mt-1">{agent.temperature || 0.7}</p>
                            </div>
                            {sessionId && (
                                <div>
                                    <span className="text-xs text-muted-foreground font-medium">Session</span>
                                    <p className="text-xs text-foreground mt-1 font-mono text-muted-foreground">{sessionId.slice(0, 8)}...</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Hidden audio element for agent voice */}
                <audio ref={audioRef} autoPlay playsInline />
            </div>
        </div>
    );
};

export default TestAgentInterface;
